<script setup lang="ts">
import RTHeader from "~/components/templates/v2/RTHeader/RTHeader.vue";
import CardPublishedIn from "~/components/templates/v2/CardPublishedIn.vue";
import EditorsExpandable from "~/components/templates/v2/EditorsExpandable.vue";
import OutlineTabs from "~/components/templates/v2/OutlineTabs/OutlineTabs.vue";
import AsideRight from "~/components/templates/v2/AsideRight.vue";
import type { IToolbarNavItemProps } from "~/components/templates/v2/ToolbarNav/ToolbarNav.models";
import type { IOutlineTabItem } from "~/components/templates/v2/OutlineTabs/OutlineTabs.models";
import { addThousands } from "@frontiers-vue3/public-pages";

const {
  showRtImpactTab,
  isImpactMetricsVisible,
  isAboutThisResearchTopicsVisible
} = useRuntimeConfig().public;
const terminology = useTerminologyStore();
const store = useMain();
const route = useRoute();

const rtDetails = store.currentResearchTopic;

const basePath = computed(
  () => `/research-topics/${route.params.rtId}/${rtDetails?.slug}`
);

const getTabText = (label: string, counter: number) =>
  counter > 0 ? `${label} ${addThousands(counter)}` : label;

const outlineItems = computed<IOutlineTabItem[]>(() => {
  const items: IOutlineTabItem[] = [];

  if (isAboutThisResearchTopicsVisible) {
    items.push({
      text: "Overview",
      path: basePath.value,
      icon: "ViewIcon"
    });
  }

  items.push({
    text: getTabText(terminology.getTerm("articles"), store.totalArticles),
    path: `${basePath.value}/articles`,
    icon: "ArticleIcon"
  });

  items.push({
    text: getTabText(terminology.getTerm("authors"), store.totalAuthors),
    path: `${basePath.value}/authors`,
    icon: "UserIcon"
  });

  if (isImpactMetricsVisible && showRtImpactTab) {
    items.push({
      text: "Impact",
      path: `${basePath.value}/impact`,
      icon: "ChartBarIcon"
    });
  }

  return items;
});

const onOutlineNavigate = (path: string) => {
  navigateTo(path);
};

const mobileNavItems = computed<IToolbarNavItemProps[]>(() =>
  outlineItems.value.map((item) => ({
    label: item.text,
    icon: item.icon || "ListIcon",
    onClick: () => navigateTo(item.path)
  }))
);
</script>

<template>
  <div class="Layout">
    <RTHeader />
    <aside class="Layout__asideLeft">
      <CardPublishedIn />
      <EditorsExpandable />
      <OutlineTabs
        :items="outlineItems"
        :current-path="route.path"
        @navigate="onOutlineNavigate" />
    </aside>
    <main
      id="main-content"
      class="Layout__main">
      <slot />
    </main>
    <AsideRight :mobile-items="mobileNavItems" />
  </div>
</template>
